import React from 'react';
import { Box, Paper, Chip, Typography } from '@mui/material';
import moment from 'moment';
import { checkAccessLevel } from '../../lib/authenticate';
import { checkShiftForRendering } from './dashboardUtils';

interface MonthViewProps {
  currentWeek: moment.Moment;
  filteredShifts: any[];
  assignments: any[];
  positions: any[];
  holidays: any[];
  availabilityMode: boolean;
  isDarkMode: boolean;
  selectedSchedule: any;
  accessLevel: string | null;
  employees?: any[];
  legendFilters?: Record<string, boolean>;
  getAvailabilityColor: (isDarkMode: boolean, date: string) => string;
  shouldShowDay: (date: moment.Moment, isHoliday: boolean, isSubmitNext: boolean) => boolean;
  onShiftClick: (shift: any, dayDate: moment.Moment) => void;
  onDateClick: (date: moment.Moment, availabilityMode: boolean) => void;
  onAddShiftClick: (date: moment.Moment) => void;
}

const MonthView: React.FC<MonthViewProps> = ({
  currentWeek,
  filteredShifts,
  assignments,
  positions,
  holidays,
  availabilityMode,
  isDarkMode,
  selectedSchedule,
  accessLevel,
  employees = [],
  legendFilters,
  getAvailabilityColor,
  shouldShowDay,
  onShiftClick,
  onDateClick,
  onAddShiftClick,
}) => {
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const firstDayOfMonth = moment(currentWeek).startOf('month');
  const daysInMonth = firstDayOfMonth.daysInMonth();
  const startDayOfWeek = firstDayOfMonth.day();
  const totalCells = Math.ceil((daysInMonth + startDayOfWeek) / 7) * 7;

  return (
    <Box sx={{ p: 3, pl: 1, pr: 0, pt: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      {/* Day Headers */}
      <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" sx={{ mb: 1, width: '100%' }}>
        {weekDays.map((day, index) => (
          <Box key={index} sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
              {day}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Calendar Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 2,
          width: '100%',
          aspectRatio: '7/6',
        }}
      >
        {Array.from({ length: totalCells }, (_, i) => {
          const dayNum = i - startDayOfWeek + 1;
          const currentDate = moment(firstDayOfMonth).date(dayNum);
          const isCurrentMonth = dayNum > 0 && dayNum <= daysInMonth;
          const isToday = isCurrentMonth && currentDate.isSame(moment(), 'day');

          const dayShifts = isCurrentMonth
            ? filteredShifts.filter(
                (shift) =>
                  (shift.isRecurring && shift?.daysOfWeek?.includes(currentDate.format('dddd').toLowerCase())) ||
                  (!shift.isRecurring && shift?.shiftDate === currentDate.format('YYYY-MM-DD'))
              )
            : [];

          const employeeCuttoffDate = moment(selectedSchedule?.activeScheduleDate).format('YYYY-MM-DD');
          const submitNextScheduleDate =
            currentDate.format('YYYY-MM-DD') ===
            moment(selectedSchedule?.activeScheduleDate).subtract(0, 'day').format('YYYY-MM-DD');

          let availabilityColor = '';
          if (availabilityMode) {
            availabilityColor = getAvailabilityColor(isDarkMode, currentDate.format('YYYY-MM-DD'));
          }
          if (currentDate < moment().subtract(2, 'day')) {
            availabilityColor += 'd4';
          }

          let holidayName = null;
          if (isCurrentMonth) {
            holidays.forEach((holiday: any) => {
              if (moment(holiday.date).format('YYYY-MM-DD') === currentDate.format('YYYY-MM-DD')) {
                holidayName = holiday.name;
              }
            });
          }

          return (
            <Paper
              key={i}
              variant="outlined"
              sx={{
                width: { xs: '110%', sm: '100%', md: '90%' },
                aspectRatio: '1/1.2',
                minHeight: 0,
                height: { xs: '110%', sm: '100%', md: '90%' },
                p: { xs: 0.5, sm: 1, md: 1.5 },
                cursor: isCurrentMonth ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: isCurrentMonth
                  ? availabilityMode
                    ? availabilityColor
                    : submitNextScheduleDate
                    ? selectedSchedule?.color+"6e"
                    : holidayName
                    ? isDarkMode
                      ? 'rgba(106, 195, 255, 0.25)'
                      : '#ffdfc5ff'
                    : isDarkMode
                    ? '#2c2c2cff'
                    : 'background.default'
                  : 'divider',
                borderColor: isToday
                  ? isDarkMode
                    ? 'info.main'
                    : 'primary.main'
                  : holidayName && isCurrentMonth
                  ? 'text.secondary'
                  : 'divider',
                borderWidth: isToday ? 2 : 1,
                transition: 'all 0.2s',
                '&:hover': isCurrentMonth
                  ? availabilityMode
                    ? { bgcolor: availabilityColor }
                    : {
                        bgcolor: submitNextScheduleDate
                          ? selectedSchedule?.color+"6e"
                          : holidayName
                          ? isDarkMode
                            ? 'rgba(106, 195, 255, 0.25)'
                            : '#ffe5d1ff'
                          : isDarkMode
                          ? '#202020ff'
                          : '#c3e5ffff',
                        boxShadow: 1,
                      }
                  : {},
                position: 'relative',
                overflow: 'hidden',
              }}
              onClick={(e) => {
                if (isCurrentMonth) {
                  if (availabilityMode) {
                    if (currentDate >= moment().subtract(2, 'day')) {
                      onDateClick(currentDate, true);
                    }
                  } else {
                    if (checkAccessLevel(accessLevel)) {
                      const isShiftClick = (e.target as HTMLElement).closest('.shift-pill');
                      if (!isShiftClick) {
                        onAddShiftClick(currentDate);
                      }
                    }
                  }
                }
              }}
            >
              {isCurrentMonth && (
                <>
                  {/* Date Number */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'start',
                      mb: { xs: 0.5, sm: 1 },
                      minHeight: { xs: 20, sm: 28, md: 32 },
                    }}
                  >
                    <Chip
                      label={`${dayNum}`}
                      size="small"
                      sx={{
                        height: { xs: 18, sm: 22, md: 24 },
                        minWidth: { xs: 18, sm: 22, md: 24 },
                        borderRadius: '50%',
                        bgcolor: isToday ? (isDarkMode ? 'info.main' : 'primary.main') : 'transparent',
                        color: isToday ? 'white' : 'text.primary',
                        fontWeight: isToday ? 700 : 600,
                        fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                    <Chip
                      label={`${holidayName ? holidayName : ''}`}
                      size="small"
                      sx={{
                        height: { xs: 18, sm: 22, md: 24 },
                        minWidth: { xs: 18, sm: 22, md: 24 },
                        borderRadius: '50%',
                        bgcolor: 'transparent !important',
                        color: isToday ? 'white' : 'text.primary',
                        fontWeight: isToday ? 700 : 600,
                        fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                  </Box>

                  {/* Shift Pills */}
                  {shouldShowDay(currentDate, !!holidayName, submitNextScheduleDate) &&
                    ((!checkAccessLevel(accessLevel) && employeeCuttoffDate >= currentDate.format('YYYY-MM-DD')) ||
                      checkAccessLevel(accessLevel)) && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: { xs: 0.25, sm: 0.5 },
                          flex: 1,
                          overflow: 'hidden',
                        }}
                      >
                        {dayShifts.slice(0, 3).map((shift, idx) => {
                          const context = {
                            shift,
                            idx,
                            dayDate: currentDate,
                            assignments,
                            positions,
                            filteredShifts,
                            accessLevel,
                            employeeCuttoffDate,
                            employees,
                          };

                          const { shouldRender, color, assignedStaffCount, assigned } = checkShiftForRendering(context);

                          if (!shouldRender) return null;
                          if (legendFilters?.assigned === false && assigned) return null;

                          // Check if shift is after next schedule start date
                          const isAfterSubmitNextScheduleDate = currentDate.isAfter(
                            moment(selectedSchedule?.activeScheduleDate),
                            'day'
                          );
                          const shiftColor = isAfterSubmitNextScheduleDate ? 'divider' : color;

                          return (
                            <Box
                              key={idx}
                              className="shift-pill"
                              onClick={(e) => {
                                e.stopPropagation();
                                onShiftClick(shift, currentDate);
                              }}
                              sx={{
                                px: { xs: 0.5, sm: 0.75 },
                                py: { xs: 0.125, sm: 0.25 },
                                borderRadius: 0.5,
                                bgcolor: shiftColor === 'divider' ? `${shiftColor}` : `${shiftColor}.light`,
                                border: 1,
                                borderColor: shiftColor === 'divider' ? `text.primary` : `${shiftColor}.main`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 0.25, sm: 0.5 },
                                minHeight: { xs: 14, sm: 16, md: 18 },
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: shiftColor === 'divider' ? `${shiftColor}` : `${shiftColor}.main`,
                                  transform: 'scale(1.02)',
                                  boxShadow: 1,
                                },
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' },
                                  fontWeight: 600,
                                  color: shiftColor === 'divider' ? 'text.primary' : 'background.paper',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: 1,
                                  lineHeight: 1,
                                }}
                              >
                                {shift.shiftName}
                              </Typography>
                              {assigned && (
                                <Box
                                  title="Assigned"
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: 'warning.main',
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                              {checkAccessLevel(accessLevel) && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: { xs: '0.5rem', sm: '0.55rem', md: '0.6rem' },
                                    color: shiftColor === 'divider' ? 'text.primary' : 'background.paper',
                                    fontWeight: 600,
                                    flexShrink: 0,
                                  }}
                                >
                                  {assignedStaffCount}/{shift.staffNeeded}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}

                        {/* More indicator */}
                        {dayShifts.length > 3 && (
                          <Typography
                            variant="caption"
                            className="shift-pill"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDateClick(currentDate, false);
                            }}
                            sx={{
                              fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' },
                              color: 'primary.main',
                              fontWeight: 600,
                              textAlign: 'center',
                              mt: { xs: 0.25, sm: 0.5 },
                              cursor: 'pointer',
                              '&:hover': {
                                color: 'primary.dark',
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            +{dayShifts.length - 3} more
                          </Typography>
                        )}
                      </Box>
                    )}
                </>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default MonthView;
